import "@/App.css";
import "@/DesignOrderRestored.css";
import { BrowserRouter, Routes, Route, Navigate } from "react-router-dom";
import { Toaster } from "sonner";
import { MsalProvider } from "@azure/msal-react";
import axios from "axios";
import { msalInstance } from "./config/msalConfig";
import { AuthProvider, useAuth } from "./contexts/AuthContext";
import { Layout } from "./components/Layout";
import { LoginPage } from "./pages/LoginPage";
import { DashboardPage } from "./pages/DashboardPage";
import { ProjectsPage } from "./pages/ProjectsPage";
import { ProjectDetailPageV7 as ProjectDetailPage } from "./pages/ProjectDetailPageV7";
import { ArchivePageV17 } from "./pages/ArchivePageV17";
import { CustomersPage } from "./pages/CustomersPage";
import { ManufacturersPage } from "./pages/ManufacturersPage";
import { SharePointPage } from "./pages/SharePointPage";
import { CalendarPage } from "./pages/CalendarPage";
import { KanbanPage } from "./pages/KanbanPage";
import { SpiroImportPage } from "./pages/SpiroImportPage";
import { AuditTrailPage } from "./pages/AuditTrailPage";
import { EmailInboxPageV2 as EmailInboxPage } from "./pages/EmailInboxPageV2";
import { UsersPage } from "./pages/UsersPage";
import { SettingsPage } from "./pages/SettingsPage";
import { HelpPageV1 as HelpPage } from "./pages/HelpPageV1";
import { CustomerIntakePage } from "./pages/CustomerIntakePage";
import { IntakeSubmissionsPage } from "./pages/IntakeSubmissionsPage";
import { IntakeReviewPageV7 as IntakeReviewPage } from "./pages/IntakeSubmissionsPageV5";
import { IntakeFormOptionsPage } from "./pages/IntakeFormOptionsPage";

import { ArchivedProjectsAdminPage } from "./pages/ArchivedProjectsAdminPage";
import { ReleaseHistoryAdminPageV53 } from "./pages/ReleaseHistoryAdminPageV53";
const normalizeSalesOpsText = (value) => {
  if (typeof value !== "string") return value;
  return value
    .replace(/Inside Sales Rep \(ISR\)/g, "Sales Ops")
    .replace(/Inside Sales Rep/g, "Sales Ops")
    .replace(/assigned ISR/g, "assigned Sales Ops user")
    .replace(/ISR review/g, "Sales Ops review")
    .replace(/ISR approval/g, "Sales Ops approval")
    .replace(/\bISR\b/g, "Sales Ops");
};

if (!axios.__inkSightSalesOpsTerminology) {
  axios.interceptors.response.use(
    (response) => response,
    (error) => {
      if (error.response?.data && typeof error.response.data.detail === "string") {
        error.response.data.detail = normalizeSalesOpsText(error.response.data.detail);
      }
      return Promise.reject(error);
    },
  );
  axios.__inkSightSalesOpsTerminology = true;
}

const LoadingScreen = () => (
  <div className="min-h-screen bg-background flex items-center justify-center">
    <div className="animate-pulse text-muted-foreground">Loading...</div>
  </div>
);

const ProtectedRoute = ({ children }) => {
  const { token, loading } = useAuth();
  if (loading) return <LoadingScreen />;
  if (!token) return <Navigate to="/login" replace />;
  return children;
};

const AdminRoute = ({ children }) => {
  const { token, loading, isAdmin } = useAuth();
  if (loading) return <LoadingScreen />;
  if (!token) return <Navigate to="/login" replace />;
  if (!isAdmin) return <Navigate to="/" replace />;
  return children;
};

const IntakeQueueRoute = ({ children, withLayout = false }) => {
  const { token, loading, canAccessIntakeQueue } = useAuth();
  if (loading) return <LoadingScreen />;
  if (!token) return <Navigate to="/login" replace />;
  if (!canAccessIntakeQueue) return <Navigate to="/" replace />;
  return withLayout ? <Layout>{children}</Layout> : children;
};

const Page = ({ children, admin = false }) => {
  const Guard = admin ? AdminRoute : ProtectedRoute;
  return (
    <Guard>
      <Layout>{children}</Layout>
    </Guard>
  );
};

function AppRoutes() {
  return (
    <AuthProvider>
      <Toaster position="top-right" theme="dark" />
      <BrowserRouter>
        <Routes>
          <Route path="/login" element={<LoginPage />} />
          <Route path="/intake" element={<CustomerIntakePage />} />
          <Route path="/projects/new" element={<Navigate to="/intake" replace />} />
          <Route
            path="/intake-submissions/:id/review"
            element={<IntakeQueueRoute><IntakeReviewPage /></IntakeQueueRoute>}
          />
          <Route path="/" element={<Page><DashboardPage /></Page>} />
          <Route path="/projects" element={<Page><ProjectsPage /></Page>} />
          <Route path="/projects/:id" element={<Page><ProjectDetailPage /></Page>} />
          <Route path="/archive" element={<Page><ArchivePageV17 /></Page>} />
          <Route path="/project-archive" element={<Page admin><ArchivedProjectsAdminPage /></Page>} />
          <Route path="/release-history" element={<Page admin><ReleaseHistoryAdminPageV53 /></Page>} />
          <Route path="/customers" element={<Page admin><CustomersPage /></Page>} />
          <Route path="/manufacturers" element={<Page admin><ManufacturersPage /></Page>} />
          <Route path="/calendar" element={<Page><CalendarPage /></Page>} />
          <Route path="/kanban" element={<Page><KanbanPage /></Page>} />
          <Route
            path="/intake-submissions"
            element={<IntakeQueueRoute withLayout><IntakeSubmissionsPage /></IntakeQueueRoute>}
          />
          <Route path="/sharepoint" element={<Page admin><SharePointPage /></Page>} />
          <Route path="/spiro" element={<Page admin><SpiroImportPage /></Page>} />
          <Route path="/audit-trail" element={<Page admin><AuditTrailPage /></Page>} />
          <Route path="/email-inbox" element={<Page admin><EmailInboxPage /></Page>} />
          <Route path="/users" element={<Page admin><UsersPage /></Page>} />
          <Route path="/intake-form-options" element={<Page admin><IntakeFormOptionsPage /></Page>} />
          <Route path="/help" element={<Page><HelpPage /></Page>} />
          <Route path="/settings" element={<Page><SettingsPage /></Page>} />
          <Route path="*" element={<Navigate to="/" replace />} />
        </Routes>
      </BrowserRouter>
    </AuthProvider>
  );
}

function App() {
  return (
    <MsalProvider instance={msalInstance}>
      <AppRoutes />
    </MsalProvider>
  );
}

export default App;
